import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMessages, useRoom } from 'dignity.js/react';
import {
  listCertificates,
  saveCertificate
} from '../../p2p/certificateStore.js';
import { certificateShards } from '../../p2p/scopes.js';
import { verifyDetachedTimeWeaveProof } from '../../p2p/timeWeave.js';

const OWNER_BATCH_SIZE = 50;
const ARCHIVIST_BATCH_SIZE = 500;

export default function CertificateShard({
  node,
  scope,
  identity,
  archivist = false,
  bootstrapPeerIds = [],
  onStored
}) {
  const batchSize = archivist ? ARCHIVIST_BATCH_SIZE : OWNER_BATCH_SIZE;
  const options = useMemo(
    () => ({
      metadata: {
        playerId: identity.playerId,
        role: archivist ? 'archivist' : 'certificate-owner',
        shard: scope
      },
      bootstrapPeerIds,
      heartbeatIntervalMs: 20000,
      ttlMs: 60000
    }),
    [archivist, bootstrapPeerIds, identity.playerId, scope]
  );
  const { joined } = useRoom(node, scope, options);
  const filter = useCallback(
    (message) =>
      ['dechess-cert-request', 'dechess-cert-batch'].includes(message.type)
      && message.payload?.shard === scope,
    [scope]
  );
  const messages = useMessages(node, filter);
  const processed = useRef(0);

  const broadcast = useCallback(
    (type, payload, peers = []) =>
      node.broadcastMessage(
        type,
        payload,
        {
          broadcastScope: scope,
          connectToPeers: peers
        }
      ),
    [node, scope]
  );

  useEffect(() => {
    if (!joined) {
      return undefined;
    }
    const request = () => {
      broadcast('dechess-cert-request', {
        shard: scope,
        requesterId: identity.playerId
      }).catch(() => {});
    };
    request();
    // A single request can leave before the shard peer is connected.
    const timer = setInterval(request, archivist ? 4000 : 5000);
    return () => clearInterval(timer);
  }, [archivist, broadcast, identity.playerId, joined, scope]);

  useEffect(() => {
    if (processed.current >= messages.length) {
      return;
    }
    const pending = messages.slice(processed.current);
    processed.current = messages.length;

    const process = async () => {
      for (const message of pending) {
        if (message.type === 'dechess-cert-request') {
          const certificates = (await listCertificates())
            .filter((certificate) => certificateShards(certificate).includes(scope))
            .slice(-batchSize);
          if (certificates.length > 0) {
            await broadcast(
              'dechess-cert-batch',
              { shard: scope, certificates },
              [message.senderId]
            );
          }
          continue;
        }

        const incoming = Array.isArray(message.payload.certificates)
          ? message.payload.certificates.slice(0, batchSize)
          : [];
        for (const certificate of incoming) {
          if (!certificateShards(certificate).includes(scope)) {
            continue;
          }
          const verificationOptions = {
            verifyVdfProof: async (proof, resultCertificate, player) =>
              (await verifyDetachedTimeWeaveProof(proof, {
                gameId: resultCertificate.gameId,
                contractHash: resultCertificate.contractHash,
                playerId: player.playerId
              })).valid
          };
          try {
            await saveCertificate(certificate, verificationOptions);
            onStored?.(certificate, scope);
          } catch {
            // Invalid, incomplete, or already irrelevant certificates are never re-served.
          }
        }
      }
    };
    process();
  }, [batchSize, broadcast, messages, onStored, scope]);

  return null;
}
